/**
 * StatusPill — small badge expressing an entity's current state.
 *
 * Used in tables (Devices status column), in dashboards, in entity detail
 * sidebars. Five canonical states cover the vast majority of cases.
 */

import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Power } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import type { StatusKind } from "../types";

export interface StatusPillProps {
  status: StatusKind;
  label?: string;
  className?: string;
}

const STATUS_META: Record<
  StatusKind,
  { icon: typeof Power; bg: string; fg: string; defaultLabel: string }
> = {
  online: {
    icon: CheckCircle2,
    bg: "bg-success/15",
    fg: "text-success",
    defaultLabel: "Online",
  },
  offline: {
    icon: Power,
    bg: "bg-surface-muted",
    fg: "text-on-surface-muted",
    defaultLabel: "Offline",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-warning/15",
    fg: "text-warning",
    defaultLabel: "Warning",
  },
  error: {
    icon: XCircle,
    bg: "bg-danger/15",
    fg: "text-danger",
    defaultLabel: "Error",
  },
  unknown: {
    icon: HelpCircle,
    bg: "bg-surface-muted",
    fg: "text-on-surface-muted",
    defaultLabel: "Unknown",
  },
};

export function StatusPill({ status, label, className }: StatusPillProps) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
        meta.bg,
        meta.fg,
        className,
      )}
    >
      <Icon className="size-3" />
      {label ?? meta.defaultLabel}
    </span>
  );
}
